/**
 * Centralized URL resolution for Rudi frontend
 *
 * Derives API, Socket, and Keycloak URLs programmatically based on hostname.
 * Supports:
 * - Remote access via Cloudflare tunnel (*.nightingale-ai.com), always on the
 *   page's own origin
 * - Container network access (rudi-frontend-prod, rudi-frontend-dev)
 * - Local development with configurable port mappings
 * - Environment variable overrides
 */

import { isElectron } from './platform';

interface ResolvedUrls {
  apiBaseUrl: string; // '' for same-origin, or e.g. http://rudi-backend-dev:3001
  socketUrl: string; // Socket.IO endpoint, e.g. https://rudi-dev.nightingale-ai.com
  keycloakUrl: string; // e.g., https://rudi-dev-keycloak.nightingale-ai.com
  isRemote: boolean; // True if accessing via Cloudflare tunnel
  isProduction: boolean; // True if prod domain (not -dev)
}

/**
 * Detect if hostname is a Cloudflare tunnel domain
 */
function isTunneledDomain(hostname: string): boolean {
  return hostname.endsWith('.nightingale-ai.com');
}

/**
 * Detect if hostname is a container network name
 * Pattern: rudi-frontend-prod, rudi-frontend-dev
 * These are only resolvable within the Docker container network
 */
function isContainerHostname(hostname: string): boolean {
  return /^rudi-frontend-(prod|dev)$/.test(hostname);
}

/**
 * Derive URLs for container network access
 * Backend listens on port 3001 internally (mapped to 7011 externally)
 * rudi-frontend-prod → rudi-backend-prod:3001, keycloak-prod:8080
 * rudi-frontend-dev → rudi-backend-dev:3001, keycloak-dev:8080
 */
function deriveContainerUrls(hostname: string): ResolvedUrls {
  const env = hostname.endsWith('-prod') ? 'prod' : 'dev';
  const backendHost = `rudi-backend-${env}`;
  const keycloakHost = `keycloak-${env}`;

  return {
    apiBaseUrl: `http://${backendHost}:3001`,
    socketUrl: `http://${backendHost}:3001`,
    keycloakUrl: `http://${keycloakHost}:8080`,
    isRemote: false,
    isProduction: env === 'prod',
  };
}

/**
 * Detect if hostname is a stack preview subdomain.
 * Cortex stack URLs use pattern: {display_slug}-{service}-{uuid}-{env}.domain
 * e.g., rudi-dev-frontend-ddc3b1a7-dev.nightingale-ai.com
 * Keep the older {repo}-{branch}-{service}-{env}-{uuid}.domain pattern too.
 */
export function isStackPreviewDomain(hostname: string): boolean {
  const subdomain = hostname.split('.')[0];
  return (
    /-frontend-[a-z0-9]+-(dev|prod)$/.test(subdomain) ||
    /-frontend-(dev|prod)-[a-z0-9]+$/.test(subdomain)
  );
}

function replacePreviewService(hostname: string, service: 'keycloak'): string {
  const parts = hostname.split('.');
  const subdomain = parts[0];
  const domain = parts.slice(1).join('.');

  const replacement = subdomain
    .replace(/-frontend-([a-z0-9]+)-(dev|prod)$/, `-${service}-$1-$2`)
    .replace(/-frontend-(dev|prod)-([a-z0-9]+)$/, `-${service}-$1-$2`);

  return `https://${replacement}.${domain}`;
}

/**
 * Derive Keycloak URL from frontend hostname
 * Pattern: rudi-dev.nightingale-ai.com -> rudi-dev-keycloak.nightingale-ai.com
 * Stack preview: replace the frontend service segment with keycloak.
 */
export function deriveKeycloakUrl(hostname: string): string {
  const parts = hostname.split('.');
  const subdomain = parts[0];
  const domain = parts.slice(1).join('.');

  // Stack preview URLs: replace "frontend" with "keycloak"
  if (isStackPreviewDomain(hostname)) {
    return replacePreviewService(hostname, 'keycloak');
  }

  return `https://${subdomain}-keycloak.${domain}`;
}

/**
 * Port configuration for local development scenarios
 */
interface PortConfig {
  frontend: number;
  backend: number;
  keycloak: number;
  isProduction: boolean;
}

/**
 * Configurable port mappings for local development
 * Add new entries here to support additional dev scenarios
 */
const PORT_CONFIGS: PortConfig[] = [
  // Production local: frontend 7010, backend 7011, keycloak 7018
  { frontend: 7010, backend: 7011, keycloak: 7018, isProduction: true },
  // Development local: frontend 7050, backend 7051, keycloak 7058
  { frontend: 7050, backend: 7051, keycloak: 7058, isProduction: false },
  // Vite dev server (container): frontend 3000, backend 7051, keycloak 7058
  { frontend: 3000, backend: 7051, keycloak: 7058, isProduction: false },
  // Vite dev server (native split): frontend 3700, backend 3701, keycloak 7058
  { frontend: 3700, backend: 3701, keycloak: 7058, isProduction: false },
];

/**
 * Find port configuration matching the frontend port
 */
function getPortConfig(port: string): PortConfig | null {
  const portNum = parseInt(port, 10);
  return PORT_CONFIGS.find(c => c.frontend === portNum) || null;
}

/**
 * Resolve all URLs based on current environment
 *
 * Priority:
 * 1. Remote access (Cloudflare tunnel) - same origin as the page
 * 2. Environment variables (VITE_API_URL, VITE_KEYCLOAK_URL)
 * 3. Port-based configuration
 * 4. Defaults
 */
function resolveUrls(): ResolvedUrls {
  // Server-side rendering fallback
  if (typeof window === 'undefined') {
    return {
      apiBaseUrl: import.meta.env.VITE_API_URL || '',
      socketUrl: import.meta.env.VITE_API_URL || '',
      keycloakUrl: import.meta.env.VITE_KEYCLOAK_URL || 'http://localhost:7058',
      isRemote: false,
      isProduction: false,
    };
  }

  const hostname = window.location.hostname;
  const port = window.location.port;

  // Every tunneled host, preview or not, serves the API and Socket.IO on its own
  // origin: the ingress routes /api and /socket.io to the backend, and the
  // frontend server proxies them where it does not. A separate api- host would
  // need its own DNS name, tunnel rule and route in every environment.
  if (isTunneledDomain(hostname)) {
    return {
      apiBaseUrl: '',
      socketUrl: window.location.origin,
      keycloakUrl: deriveKeycloakUrl(hostname),
      isRemote: true,
      isProduction: !hostname.includes('-dev'),
    };
  }

  // Container network access (e.g., from Browserless or other containers)
  // These hostnames are only resolvable within the Docker network
  if (isContainerHostname(hostname)) {
    return deriveContainerUrls(hostname);
  }

  // Local access - check port config or use env vars
  const portConfig = getPortConfig(port);

  // In local dev mode, prefer using empty API URL (relative paths)
  // This lets the Vite proxy handle routing to the backend, avoiding CORS issues
  // Only use explicit URL if VITE_API_URL is set to a different host
  let apiUrl = '';
  const envApiUrl = import.meta.env.VITE_API_URL;

  // The installed shell proxies to the server selected in its setup screen.
  if (envApiUrl && !isElectron()) {
    // Check if the env URL points to a different host (not localhost with same-ish port)
    // If it's just localhost:backend_port, use relative URLs instead
    try {
      const envUrl = new URL(envApiUrl);
      const isLocalBackend = envUrl.hostname === 'localhost' || envUrl.hostname === '127.0.0.1';
      if (!isLocalBackend) {
        // Different host - use the full URL
        apiUrl = envApiUrl;
      }
      // For localhost URLs, leave apiUrl empty to use Vite proxy
    } catch {
      // Invalid URL, use as-is
      apiUrl = envApiUrl;
    }
  }

  const keycloakUrl =
    import.meta.env.VITE_KEYCLOAK_URL ||
    (portConfig ? `http://localhost:${portConfig.keycloak}` : 'http://localhost:7058');

  return {
    apiBaseUrl: apiUrl,
    socketUrl: apiUrl || window.location.origin,
    keycloakUrl,
    isRemote: false,
    isProduction: portConfig?.isProduction ?? false,
  };
}

// Singleton for caching resolved URLs (computed once per page load)
let cachedUrls: ResolvedUrls | null = null;

/**
 * Get resolved URLs (cached)
 */
function getUrls(): ResolvedUrls {
  if (!cachedUrls) {
    cachedUrls = resolveUrls();
    // Always log in dev for debugging
    console.log('[url-resolver] API Base URL:', cachedUrls.apiBaseUrl || '(empty - using proxy)');
    console.log('[url-resolver] Socket URL:', cachedUrls.socketUrl);
    console.log('[url-resolver] Keycloak URL:', cachedUrls.keycloakUrl);
  }
  return cachedUrls;
}

// Convenience exports for common use cases
export const getApiBaseUrl = (): string => getUrls().apiBaseUrl;
export const getSocketUrl = (): string => getUrls().socketUrl;
export const getKeycloakUrl = (): string => getUrls().keycloakUrl;
